import { GetString } from 'fluent-react';
import React, { RefObject, useCallback, useState } from 'react';
import styled from 'styled-components';
import {
  Product,
  ProductClass,
  ProductLevel,
  TreeMapInput,
  TreeMapProduct,
  TreeMapType,
} from '../../graphQL/types/shared';
import { gql, useQuery } from '../../graphQL/useQuery';
import { SlideElementProps } from '../../presentationManager/PresentationManager';
import ChartWrapper from './ChartWrapper';
import {
  ChartContainer,
  Grid,
} from './Grid';
import LabelsWrapper from './LabelsWrapper';

const query = gql`
  query MarketShareChart(
    $treeMapFacet: TreeMapType!,
    $treeMapProductClass: ProductClass,
    $treeMapYear: Int,
    $treeMapProductLevel: ProductLevel,
    $treeMapLocationLevel: LocationLevel,
    $treeMapLocation: ID,
    $treeMapProduct: ID,
    $treeMapPartner: ID,
    $id: ID!,
    $sectorsProductClass: ProductClass!,
    $sectorsProductLevel: ProductLevel,
    ) {
    chartData: treeMap(
      facet: $treeMapFacet,
      productClass: $treeMapProductClass,
      year: $treeMapYear,
      productLevel: $treeMapProductLevel,
      locationLevel: $treeMapLocationLevel,
      location: $treeMapLocation,
      product: $treeMapProduct,
      partner: $treeMapPartner,
    ) {
      ... on TreeMapProduct {
        product {
          id
          shortName
          topLevelParent {
            id
          }
        }
        year
        globalMarketShare
      }
    }
    location(id: $id) {
      id
      shortName
    }
    sectors: allProducts(
      productClass: $sectorsProductClass,
      productLevel: $sectorsProductLevel,
    ) {
      id
      shortName
    }
  }
`;

export interface ChartFetchedDatum {
  product: {
    id: TreeMapProduct['product']['id']
    shortName: TreeMapProduct['product']['shortName'],
    topLevelParent: {
      id: Product['topLevelParent']['id'],
    },
  };
  year: TreeMapProduct['year'];
  globalMarketShare: TreeMapProduct['globalMarketShare'];
}

export interface SectorFetchedDatum {
  id: Product['id'];
  shortName: Product['shortName'];
}

export interface SuccessResponse {
  chartData: ChartFetchedDatum[];
  sectors: SectorFetchedDatum[];
}

export interface Result {
  loading: boolean;
  error?: any;
  data: SuccessResponse | undefined;
}

export interface Variables {
  treeMapFacet: TreeMapInput['facet'];
  treeMapProductClass: TreeMapInput['productClass'];
  treeMapYear: TreeMapInput['year'];
  treeMapProductLevel: TreeMapInput['productLevel'];
  treeMapLocationLevel: TreeMapInput['locationLevel'];
  treeMapLocation: TreeMapInput['location'];
  treeMapProduct: TreeMapInput['product'];
  treeMapPartner: TreeMapInput['partner'];
  id: string;
  sectorsProductClass: ProductClass;
  sectorsProductLevel: ProductLevel | null;
}

const zIndices = {
  chartAreaMeasurement: 10,
  verticalLineContainer: 20,
  chart: 30,
  tooltip: 40,
  loadingSpinner: 50,
  errorOverlay: 60,
};

//#region Styling
const ChartAreaMeasurementEl = styled(ChartContainer)`
  z-index: ${zIndices.chartAreaMeasurement};
`;
//#endregion

export interface DOMMeasurement {
  width: number;
  height: number;
}

export interface MouseCoords {
  x: number;
  y: number;
}

interface Props extends SlideElementProps {
  countryId: string;
  zIndex: number;
  getFluentString: GetString;
}

const QueryWrapper = React.forwardRef((props: Props, rootElRef: RefObject<HTMLDivElement>) => {
  const {
    countryId, zIndex, getFluentString,
  } = props;
  const [selectedSectors, setSelectedSectors] = useState<string[] | undefined>(undefined);
  const [chartSize, setChartSize] = useState<DOMMeasurement | undefined>(undefined);
  const [mouseCoords, setMouseCoords] = useState<MouseCoords | undefined>(undefined);
  const variables: Variables = {
    treeMapFacet: TreeMapType.CPY_C,
    treeMapProductClass: ProductClass.HS,
    treeMapYear: null,
    treeMapProductLevel: ProductLevel.section,
    treeMapLocationLevel: null,
    treeMapLocation: countryId,
    treeMapProduct: null,
    treeMapPartner: null,
    id: countryId,
    sectorsProductClass: ProductClass.HS,
    sectorsProductLevel: ProductLevel.section,
  };
  const result = useQuery<SuccessResponse, Variables>(query, {variables});

  const selectSector = (sector: string) => {
    if (selectedSectors !== undefined) {
      const newSectors = new Set(selectedSectors);
      newSectors.add(sector);
      setSelectedSectors([...newSectors].sort());
    }
  };

  const unselectSector = (sector: string) => {
    if (selectedSectors !== undefined) {
      const newSectors = new Set(selectedSectors);
      newSectors.delete(sector);
      setSelectedSectors([...newSectors].sort());
    }
  };

  const measureChartArea = useCallback((el: HTMLDivElement | null) => {
    if (el !== null) {
      const {width, height} = el.getBoundingClientRect();
      setChartSize({
        width: Math.floor(width),
        height: Math.floor(height),
      });
    }
  }, []);

  const style: React.CSSProperties = {
    zIndex,
  };

  return (
    <Grid ref={rootElRef} style={style}>
      <ChartAreaMeasurementEl ref={measureChartArea}/>
      <ChartWrapper
      extraInputs={{
        chartSize,
        chartZIndex: zIndices.chart,
        spinnerZIndex: zIndices.loadingSpinner,
        errorOverlayZIndex: zIndices.errorOverlay,
        selectedSectors,
        setMouseCoords,
        mouseCoords,
        tooltipZIndex: zIndices.tooltip,
        verticalLineContainerZIndex: zIndices.verticalLineContainer,
        getFluentString,
      }}
      result={result}
    />
    <LabelsWrapper
      extraInputs={{
        selectedSectors,
        setSelectedSectors,
        selectSector,
        unselectSector,
        getFluentString,
      }}
      result={result}
    />
    </Grid>
  );
});

export default QueryWrapper;
